import { Formik, FormikHelpers } from 'formik';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { postNewAsset } from 'src/api/stoApi';
import { AssetEnergyDemand } from 'src/components/assets/addedit-forms/EnergyDemand';
import { AssetFloorplanDescription } from 'src/components/assets/addedit-forms/FloorplanDescription';
import { AssetGeneralInfo } from 'src/components/assets/addedit-forms/GeneralInfo';
import { AssetLocationDetails } from 'src/components/assets/addedit-forms/LocationDetails';
import { AssetPropertyOverview } from 'src/components/assets/addedit-forms/PropertyOverview';
import { AssetPropertyPhotos } from 'src/components/assets/addedit-forms/PropertyPhotos';
import { AssetRentalIncome } from 'src/components/assets/addedit-forms/RentalIncome';
import { AssetUploadPropertyDocuments } from 'src/components/assets/addedit-forms/UploadPropertyDocuments';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { TwoColumnGridContainer } from 'src/components/ui/TwoColumnGridContainer';
import { Routes } from 'src/router/Routes';
import { generalStore } from 'src/stores/GeneralStore';
import { removeNullUndefined } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Alert, Button, Snackbar } from '@mui/material';
import { withZodSchema } from 'formik-validator-zod';
import { AssetFormError } from 'src/components/assets/form-components/AssetFormError';
import { AssetFormActionsContainer } from 'src/components/assets/form-components/AssetFormActionsContainer';
import { upsertAssetSchema, POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';
import { PowerplantOverview } from 'src/components/assets/powerplant/PowerplantOverview';
import { PowerplantDetails } from 'src/components/assets/powerplant/PowerplantDetails';
import { PlotOverview } from 'src/components/assets/plot/PlotOverview';
import { AssetTypeConditional } from 'src/components/user/AssetTypeConditional';
import { AxiosError } from 'axios';
import { AssetTypeSelector } from 'src/components/assets/AssetTypeSelector';
import { useAssetType } from 'src/context-providers/asset-type/AssetTypeContext';
import { getDefaultData } from '@brickwise/asset-validation/dist/utils';

export const AddAssetPage: FC = () => {
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { assetType } = useAssetType();

  const preprocessValues = useCallback(
    async (values: POST_WITH_ID): Promise<POST_WITH_ID> => {
      const newAsset = removeNullUndefined(upsertAssetSchema.parse({ ...values, type: assetType }));
      if (assetType !== 'citizenParticipationLoan') {
        delete newAsset.rentalDetails?.rentalReturn;
      }
      return newAsset;
    },
    [assetType],
  );

  const continueClicked = (formikHelpers: FormikHelpers<any>) => async (newAsset: POST_WITH_ID) => {
    // post new asset
    generalStore.setIsLoading(true);
    try {
      const response = await postNewAsset(newAsset);
      const data = response.data;
      setOpenSuccessAlert(true);
      navigate(Routes.ASSETS.INVESTMENT.replace(':assetId', data.data.id));
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.response?.data?.errorMapping) {
          formikHelpers.setErrors(error.response.data.errorMapping);
        }
      }
      console.log(error);
      generalStore.setError('Error while creating new asset.');
    } finally {
      generalStore.setIsLoading(false);
    }
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.ADD,
            label: t('dashboard.breadcrumbs.addasset'),
          },
        ]}
        headline={t('dashboard.breadcrumbs.addasset')}
      ></SiteHeader>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setOpenSuccessAlert(false)}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('dashboard.assets.addedit.successalert')}
        </Alert>
      </Snackbar>
      <Formik
        initialValues={getDefaultData(upsertAssetSchema)}
        validate={(values) => {
          const result = withZodSchema(upsertAssetSchema)(values);
          // We log this so we can debug stuff on the production as well.
          console.log(result);
          return result;
        }}
        onSubmit={async (values, formikHelpers) => {
          const preprocessedValues = await preprocessValues(values);
          await continueClicked(formikHelpers)(preprocessedValues);
        }}
      >
        {(props) => {
          return (
            <form onSubmit={props.handleSubmit}>
              <AssetTypeSelector disabled={false} />
              <AssetGeneralInfo disabled={false} />
              <AssetTypeConditional supported={['powerplant', 'powerplantPortfolio', 'citizenParticipationLoan']}>
                <TwoColumnGridContainer>
                  <PowerplantOverview disabled={false} />
                  <PowerplantDetails disabled={false} />
                </TwoColumnGridContainer>
              </AssetTypeConditional>

              <AssetTypeConditional supported={['realEstate', 'builderOwnerModel']}>
                <TwoColumnGridContainer>
                  <AssetPropertyOverview disabled={false} />
                  <AssetEnergyDemand disabled={false} />
                </TwoColumnGridContainer>
              </AssetTypeConditional>

              <AssetTypeConditional supported="plot">
                <PlotOverview disabled={false} />
              </AssetTypeConditional>

              <AssetTypeConditional supported="realEstate">
                <AssetFloorplanDescription disabled={false} />
              </AssetTypeConditional>
              <AssetRentalIncome disabled={false} />
              <AssetTypeConditional supported={['realEstate', 'powerplant', 'builderOwnerModel', 'citizenParticipationLoan', 'plot']}>
                <AssetLocationDetails disabled={false} />
              </AssetTypeConditional>

              <AssetUploadPropertyDocuments disabled={false} />
              <AssetPropertyPhotos disabled={false} />

              <AssetFormError />

              <AssetFormActionsContainer>
                <Button variant="contained" disableElevation color="inherit" onClick={() => props.resetForm()}>
                  {t('dashboard.assets.addedit.main.reset')}
                </Button>

                <Button variant="contained" color="primary" disableElevation type="submit" data-cy="asset-add-continue-button">
                  {t('dashboard.assets.addedit.main.continue')}
                </Button>
              </AssetFormActionsContainer>
            </form>
          );
        }}
      </Formik>
    </>
  );
};
